import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import { isValidUsername } from '@/lib/auth-helpers'
import {Plus, Trash2, Loader2, X, ShieldCheck, User, Pencil} from 'lucide-react'
import toast from 'react-hot-toast'
import type { UserProfile } from '@/types'

const PERMISSION_LABELS: Record<string, string> = {
  admin: 'Administrador',
  editor: 'Editor',
  author: 'Autor',
}

const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  admin: 'Acesso total ao painel',
  editor: 'Pode editar todas as notícias',
  author: 'Pode criar e editar as suas notícias',
}

const emptyForm = {
  displayName: '',
  username: '',
  password: '',
  confirmPassword: '',
  permission: 'author' as UserProfile['permission'],
}

const emptyEditForm = {
  displayName: '',
  permission: 'author' as UserProfile['permission'],
  password: '',
  confirmPassword: '',
}

export default function ManageUsers() {
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(true)

  // criar
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // editar
  const [editTarget, setEditTarget] = useState<UserProfile | null>(null)
  const [editForm, setEditForm] = useState(emptyEditForm)
  const [editSaving, setEditSaving] = useState(false)
  const [editErrors, setEditErrors] = useState<Record<string, string>>({})

  // eliminar
  const [confirmDelete, setConfirmDelete] = useState<UserProfile | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.user_profiles.list({
        sort: { createdAt: -1 },
        limit: 100,
      })
      setUsers(list as UserProfile[])
    } catch {
      toast.error('Erro ao carregar utilizadores.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  /* ── Validação: criar ── */
  const validate = (): boolean => {
    const errs: Record<string, string> = {}
    if (!form.displayName.trim()) errs.displayName = 'O nome é obrigatório.'
    if (!form.username.trim()) errs.username = 'O nome de utilizador é obrigatório.'
    else if (!isValidUsername(form.username)) errs.username = 'Só letras, números, pontos, hífens e underscores (3–32 caracteres).'
    if (!form.password) errs.password = 'A senha é obrigatória.'
    else if (form.password.length < 6) errs.password = 'A senha deve ter pelo menos 6 caracteres.'
    if (form.confirmPassword !== form.password) errs.confirmPassword = 'As senhas não coincidem.'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  /* ── Validação: editar ── */
  const validateEdit = (): boolean => {
    const errs: Record<string, string> = {}
    if (!editForm.displayName.trim()) errs.displayName = 'O nome é obrigatório.'
    if (editForm.password && editForm.password.length < 6) errs.password = 'A senha deve ter pelo menos 6 caracteres.'
    if (editForm.password && editForm.confirmPassword !== editForm.password) errs.confirmPassword = 'As senhas não coincidem.'
    setEditErrors(errs)
    return Object.keys(errs).length === 0
  }

  /* ── Criar utilizador ── */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: {
          action: 'create_profile',
          username: form.username.trim(),
          password: form.password,
          displayName: form.displayName.trim(),
          permission: form.permission,
          email: `${form.username.toLowerCase().trim()}@rcf.internal`,
        },
      }) as any

      if (result?.error) throw new Error(result.error)
      toast.success(`Utilizador "${form.displayName}" criado com sucesso!`)
      setForm(emptyForm)
      setErrors({})
      setShowForm(false)
      fetchUsers()
    } catch (err: any) {
      toast.error(err?.data?.error || err?.message || 'Erro ao criar utilizador.')
    } finally {
      setSaving(false)
    }
  }

  /* ── Abrir modal de edição ── */
  const openEdit = (u: UserProfile) => {
    setEditTarget(u)
    setEditForm({
      displayName: u.displayName || '',
      permission: u.permission || 'author',
      password: '',
      confirmPassword: '',
    })
    setEditErrors({})
  }

  /* ── Guardar edição ── */
  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editTarget || !validateEdit()) return
    setEditSaving(true)
    try {
      const result = await lumi.functions.invoke('createAdminUser', {
        method: 'POST',
        body: {
          action: 'update_profile',
          profileId: editTarget._id,
          displayName: editForm.displayName.trim(),
          permission: editForm.permission,
          ...(editForm.password ? { password: editForm.password } : {}),
        },
      }) as any

      if (result?.error) throw new Error(result.error)
      toast.success('Utilizador atualizado com sucesso!')
      setEditTarget(null)
      fetchUsers()
    } catch (err: any) {
      toast.error(err?.data?.error || err?.message || 'Erro ao atualizar utilizador.')
    } finally {
      setEditSaving(false)
    }
  }

  /* ── Eliminar ── */
  const handleDelete = async (profile: UserProfile) => {
    setDeleting(profile._id)
    try {
      await lumi.entities.user_profiles.delete(profile._id)
      toast.success('Perfil eliminado com sucesso.')
      setUsers(prev => prev.filter(u => u._id !== profile._id))
    } catch {
      toast.error('Erro ao eliminar perfil.')
    } finally {
      setDeleting(null)
      setConfirmDelete(null)
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Cabeçalho */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Utilizadores</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {users.length} utilizador(es) cadastrado(s)
          </p>
        </div>
        <button
          onClick={() => { setForm(emptyForm); setErrors({}); setShowForm(true) }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <Plus size={16} />
          Novo Utilizador
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-red-600" />
          </div>
        ) : users.length === 0 ? (
          <div className="py-16 text-center">
            <User size={32} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-sm text-gray-400">Nenhum utilizador cadastrado ainda.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {users.map(u => (
              <div key={u._id} className="flex items-center gap-4 px-5 py-4">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-950 flex items-center justify-center flex-shrink-0">
                  <span className="text-red-700 dark:text-red-400 font-bold text-sm">
                    {u.displayName?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {u.displayName}
                    {u.isBlocked ? (
                      <span className="ml-2 inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700 dark:bg-red-950 dark:text-red-400">
                        Bloqueado
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">
                    @{(u as any).username || '—'}
                  </p>
                </div>
                {/* Permissão + Acções */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${
                    u.permission === 'admin'
                      ? 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-400'
                      : u.permission === 'editor'
                      ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}>
                    <ShieldCheck size={11} />
                    {PERMISSION_LABELS[u.permission] || u.permission}
                  </span>
                  {/* Botão Editar */}
                  <button
                    onClick={() => openEdit(u)}
                    className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 transition-all"
                    aria-label="Editar utilizador"
                    title="Editar"
                  >
                    <Pencil size={15} />
                  </button>
                  {/* Botão Eliminar */}
                  <button
                    onClick={() => setConfirmDelete(u)}
                    disabled={deleting === u._id}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 transition-all"
                    aria-label="Eliminar perfil"
                    title="Eliminar"
                  >
                    {deleting === u._id
                      ? <Loader2 size={15} className="animate-spin" />
                      : <Trash2 size={15} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modal: Novo Utilizador ── */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Novo Utilizador</h3>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Nome <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 font-normal ml-1">(aparecerá como autor)</span>
                </label>
                <input type="text" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                  placeholder="Ex: Carlos Felipe" autoFocus
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                {errors.displayName && <p className="text-xs text-red-500 mt-1">{errors.displayName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Utilizador (Login) <span className="text-red-500">*</span>
                </label>
                <input type="text" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="Ex: carlos.felipe" spellCheck={false} autoComplete="off"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                {errors.username
                  ? <p className="text-xs text-red-500 mt-1">{errors.username}</p>
                  : <p className="text-xs text-gray-400 mt-1">Letras, números, pontos, hífens ou underscores.</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Senha <span className="text-red-500">*</span>
                </label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Mínimo 6 caracteres" autoComplete="new-password"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Confirmar Senha <span className="text-red-500">*</span>
                </label>
                <input type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                  placeholder="Repita a senha" autoComplete="new-password"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Nível de Permissão</label>
                <select value={form.permission} onChange={e => setForm(f => ({ ...f, permission: e.target.value as UserProfile['permission'] }))}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors">
                  {Object.entries(PERMISSION_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label} — {PERMISSION_DESCRIPTIONS[val]}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : null}
                  Criar Utilizador
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Editar Utilizador ── */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Editar Utilizador</h3>
                <p className="text-xs text-gray-400 mt-0.5">@{(editTarget as any).username}</p>
              </div>
              <button onClick={() => setEditTarget(null)} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleEditSave} className="space-y-4" noValidate>
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Nome <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-400 font-normal ml-1">(aparecerá como autor)</span>
                </label>
                <input type="text" value={editForm.displayName} onChange={e => setEditForm(f => ({ ...f, displayName: e.target.value }))}
                  placeholder="Nome do utilizador" autoFocus
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                {editErrors.displayName && <p className="text-xs text-red-500 mt-1">{editErrors.displayName}</p>}
              </div>

              {/* Permissão */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Nível de Permissão</label>
                <select value={editForm.permission} onChange={e => setEditForm(f => ({ ...f, permission: e.target.value as UserProfile['permission'] }))}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors">
                  {Object.entries(PERMISSION_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label} — {PERMISSION_DESCRIPTIONS[val]}</option>
                  ))}
                </select>
              </div>

              {/* Nova senha (opcional) */}
              <div className="border-t border-gray-100 dark:border-gray-800 pt-4">
                <p className="text-xs text-gray-400 mb-3">Deixe em branco para manter a senha actual.</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Nova Senha</label>
                    <input type="password" value={editForm.password} onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
                      placeholder="Mínimo 6 caracteres" autoComplete="new-password"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                    {editErrors.password && <p className="text-xs text-red-500 mt-1">{editErrors.password}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Confirmar Nova Senha</label>
                    <input type="password" value={editForm.confirmPassword} onChange={e => setEditForm(f => ({ ...f, confirmPassword: e.target.value }))}
                      placeholder="Repita a nova senha" autoComplete="new-password"
                      className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors" />
                    {editErrors.confirmPassword && <p className="text-xs text-red-500 mt-1">{editErrors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setEditTarget(null)}
                  className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  Cancelar
                </button>
                <button type="submit" disabled={editSaving}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {editSaving ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />}
                  Guardar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Confirmar eliminação ── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Eliminar Perfil</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
              Deseja eliminar o perfil de{' '}
              <strong className="text-gray-700 dark:text-gray-300">"{confirmDelete.displayName}"</strong>?
              Esta acção é permanente.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDelete)} disabled={!!deleting}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {deleting ? <Loader2 size={15} className="animate-spin" /> : null}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
